import { Button, Drawer, Loading, Title } from "@components";

import styles from "styles/pages/reports/reports.module.scss";
import IdentificationGroup from "./IdentificationGroup";
import IdentificationRegistrationData from "./IdentificationRegistrationData";
import TransitionsIdentificationGroups from "./TransitionsIdentificationGroups";
import { useEffect, useState } from "react";
import { MonthYearChart } from "pages/Reports/MonthYearChart";
import { ReportsAPI } from "providers/ReportsAPI";
import { Numbers } from "shared/utils/Numbers";
import { If } from "shared/components/control-statements";
import dayjs from "dayjs";

const Trend = ({ growth }) => {
  const isNegative = growth < 0;
  const iconClass = isNegative
    ? "fas fa-arrow-trend-down"
    : "far fa-arrow-trend-up";
  const iconColor = isNegative ? "#e48186" : "#51D9A3";
  const sign = growth > 0 ? "+" : "";
  return (
    <>
      {sign}
      {Numbers.number(growth, 2)}%{" "}
      <i className={iconClass} style={{ color: iconColor }} />
    </>
  );
};

const BigNumberTitles = {
  identifiable_customers: "Total de clientes identificados",
  max_identification_tax: "Taxa de identificação máxima",
  activable_customer_tax: "Taxa de clientes ativáveis",
};

const BigNumberTooltips = {
  identifiable_customers:
    "Quantidade de clientes com ao menos 1 dado de identificação.",
  max_identification_tax:
    "Porcentagem de clientes com todos os dados de identificação.",
  activable_customer_tax: "Porcentagem de clientes com Telefone ou E-mail",
};

const BigNumber = ({ data, onClick }) => {
  return (
    <div className={styles.bigNumber}>
      <div className="d-flex flex-column">
        <div className="d-flex align-center">
          <p className={styles.number}>{data.value}</p>
          <div
            className={styles.growth}
            title="Variação percentual dos últimos 30 dias."
          >
            <Trend growth={data.growth} />
          </div>
        </div>
        <div className={styles.title}>
          <p>{BigNumberTitles[data.alias]}</p>
          <span className={styles.info} title={BigNumberTooltips[data.alias]}>
            <i className="fal fa-info-circle" />
          </span>
        </div>
      </div>
      <Button design="default-icon-mini-2" onClick={onClick}>
        <i className="far fa-chart-waterfall" />
      </Button>
    </div>
  );
};

const numbers = (stats) => {
  return [
    {
      alias: "identifiable_customers",
      value: Numbers.formatInt(stats?.identifiable_customers?.value || 0),
      growth: stats?.identifiable_customers?.growth || 0,
    },
    {
      alias: "max_identification_tax",
      value: Numbers.number(stats?.max_identification_tax?.value || 0, 2) + "%",
      growth: stats?.max_identification_tax?.growth || 0,
    },
    {
      alias: "activable_customer_tax",
      value: Numbers.number(stats?.activable_customer_tax?.value || 0, 2) + "%",
      growth: stats?.activable_customer_tax?.growth || 0,
    },
  ];
};

export default function Index() {
  const [selectedData, setSelectedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingHistoric, setLoadingHistoric] = useState(false);
  const [data, setData] = useState(null);

  const fetchData = async () => {
    const provider = new ReportsAPI();
    const response = await provider.customerGroups();

    setData(response);
    setLoading(false);
  };

  const fetchHistory = async ({ year, month }) => {
    setLoadingHistoric(true);
    const provider = new ReportsAPI();
    const results = await provider.customerHistory("keynumbers", year, month);

    const limit = month ? dayjs(`${year}-${month}`).endOf("month").date() : 12;
    const rows = Array(limit).fill(null);

    for (const result of results) {
      const parsedDate = month
        ? dayjs(result.date, "DD/MM/YYYY")
        : dayjs(result.date, "MM/YYYY");

      const index = month ? parsedDate.date() : parsedDate.month() + 1;

      if (index >= 1 && index <= limit) {
        const data = result[selectedData.alias];

        rows[index - 1] = {
          value: data ?? 0,
        };
      }
    }

    setLoadingHistoric(false);
    return rows;
  };
  const fetchGroupHistory = async (key, { year, month }) => {
    setLoadingHistoric(true);
    const provider = new ReportsAPI();
    const results = await provider.customerHistory("groups", year, month);

    const limit = month ? dayjs().month(month).endOf("month").date() : 12;

    const rows = Array(limit).fill(null);

    if (!results || !results.length) {
      setLoadingHistoric(false);
      return rows;
    }

    for (const result of results) {
      if (!result?.date) continue;

      const format = month ? "DD-MM-YYYY" : "MM-YYYY";
      const date = dayjs(result.date, format);
      const index = month ? date.date() : date.month() + 1;

      if (index >= 1 && index <= limit) {
        if (key === "statistics" && result.statistics) {
          rows[index - 1] = Object.fromEntries(
            Object.entries(result.statistics).map(([statKey, statObj]) => {
              return [statKey, Number(statObj?.value ?? 0)];
            })
          );
        } else {
          rows[index - 1] = Object.fromEntries(
            Object.entries(result)
              .map(([entryKey, obj]) => {
                if (entryKey === "date") return null;
                return [entryKey, Number(obj?.value ?? 0)];
              })
              .filter(Boolean)
          );
        }
      }
    }

    setLoadingHistoric(false);
    return rows;
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      <Title
        title="Clientes Identificados"
        backTo={{ url: "/relatorios/clientes", name: "Clientes" }}
      />

      <If cond={loading}>
        <Loading fullContent />
      </If>

      <If cond={!loading}>
        <div className={styles.bigNumbersIdentification}>
          {numbers(data?.statistics || {}).map((item) => (
            <BigNumber
              data={item}
              key={item.alias}
              onClick={() => {
                setSelectedData({
                  name: BigNumberTitles[item.alias],
                  alias: item.alias,
                });
              }}
            />
          ))}
        </div>

        <div className="d-flex justify-between gap-15">
          <IdentificationGroup
            data={data?.groups?.groups || {}}
            loading={loadingHistoric}
            onFetchHistory={(...params) =>
              fetchGroupHistory("groups", ...params)
            }
          />
          <IdentificationRegistrationData
            data={data?.groups?.statistics || {}}
            loading={loadingHistoric}
            onFetchHistory={(...params) =>
              fetchGroupHistory("statistics", ...params)
            }
          />
        </div>

        <TransitionsIdentificationGroups data={data?.transitions || {}} />
      </If>

      <Drawer
        visible={selectedData}
        onClose={() => setSelectedData(null)}
        withoutTransition
        direction="full-center"
        showCloseButton={false}
      >
        <MonthYearChart
          selectedData={selectedData}
          onClose={() => setSelectedData(false)}
          onFetchData={fetchHistory}
          loading={loadingHistoric}
          startOn2025
          startOnMonth={6}
          startOnYear={2025}
        />
      </Drawer>
    </>
  );
}
